import type {EcommerceSalesChannel} from '@/types/salesChannels';
import type {LightspeedSalesChannel} from '@/types/salesChannels/lightspeedSalesChannels';
import type {DelegationApiKeyCredentials, PostRegisterApiKeyResponse} from '@myparcel-frontend/delegation-service';
import type {
  CreateSalesChannelRequest,
  ImportProductsRequest,
  PostRequestDelegationResponse,
  RenameSalesChannelRequest,
} from '@myparcel-frontend/lightspeed-ecommerce-service';
import {useDelegationService} from '@myparcel-frontend/delegation-service';
import {useLightspeedEcommerceService} from '@myparcel-frontend/lightspeed-ecommerce-service';
import {useSalesChannelStore} from '../../store/salesChannelStore/salesChannelStore';
import logToMonitor from '../../utils/logging';

export default function useLightspeedEcommerceSalesChannels() {
  const salesChannelStore = useSalesChannelStore();

  const getLightspeedSalesChannels = async (shopIds: string[]): Promise<LightspeedSalesChannel[]> => {
    const client = useLightspeedEcommerceService();

    try {
      const response = await client.getSalesChannels(shopIds);
      const result: LightspeedSalesChannel[] = [];

      if (!response || !response?.results?.length) {
        return result;
      }

      for (const salesChannel of response.results) {
        const lightspeedSalesChannel: LightspeedSalesChannel = {
          ...salesChannel,
          ecommercePlatform: 'LIGHTSPEED',
        };

        const newSalesChannel = salesChannelStore.addSalesChannel(lightspeedSalesChannel);

        result.push(newSalesChannel);
      }

      return result;
    } catch (error) {
      logToMonitor(error as Error, {
        _myparcel_extra_info: 'Fetching Lightspeed sales channels failed in useLightspeed composable',
      });

      throw error;
    }
  };

  const createLightspeedSalesChannel = async (data: CreateSalesChannelRequest) => {
    const client = useLightspeedEcommerceService();

    try {
      const response = await client.createSalesChannel(data);

      const newLightspeedSalesChannel: LightspeedSalesChannel = {
        ...response,
        ecommercePlatform: 'LIGHTSPEED',
      };

      salesChannelStore.addSalesChannel(newLightspeedSalesChannel);

      return response;
    } catch (error) {
      logToMonitor(error as Error, {
        _myparcel_extra_info: 'Creating Lightspeed sales channel failed in useLightspeed composable',
        _myparcel_sales_channel_payload: data,
      });

      throw error;
    }
  };

  const renameSalesChannel = async (data: RenameSalesChannelRequest) => {
    const client = useLightspeedEcommerceService();

    try {
      const response = await client.renameSalesChannel(data);

      const updatedLightspeedSalesChannel: LightspeedSalesChannel = {
        ...response,
        ecommercePlatform: 'LIGHTSPEED',
      };

      salesChannelStore.updateSalesChannel(updatedLightspeedSalesChannel);

      return response;
    } catch (error) {
      logToMonitor(error as Error, {
        _myparcel_extra_info: 'Renaming Lightspeed sales channel failed in useLightspeed composable',
        _myparcel_sales_channel_id: data.salesChannelId,
        _myparcel_new_name: data.name,
      });

      throw error;
    }
  };

  const removeLightspeedSalesChannel = async (salesChannelId: string) => {
    const client = useLightspeedEcommerceService();

    try {
      const response = await client.removeSalesChannel(salesChannelId);

      salesChannelStore.removeSalesChannel(salesChannelId);

      return response;
    } catch (error) {
      logToMonitor(error as Error, {
        _myparcel_extra_info: 'Removing Lightspeed sales channel failed in useLightspeed composable',
        _myparcel_sales_channel_id: salesChannelId,
      });

      throw error;
    }
  };

  const enableOrderLoading = async (salesChannelId: string) => {
    const client = useLightspeedEcommerceService();

    try {
      const response = await client.enableOrderLoading(salesChannelId);

      salesChannelStore.enableOrderLoading(salesChannelId);

      return response;
    } catch (error) {
      logToMonitor(error as Error, {
        _myparcel_extra_info: 'Enabling order loading for Lightspeed sales channel failed',
        _myparcel_sales_channel_id: salesChannelId,
      });

      return undefined;
    }
  };

  const disableOrderLoading = async (salesChannelId: string) => {
    const client = useLightspeedEcommerceService();

    try {
      const response = await client.disableOrderLoading(salesChannelId);

      salesChannelStore.disableOrderLoading(salesChannelId);

      return response;
    } catch (error) {
      logToMonitor(error as Error, {
        _myparcel_extra_info: 'Disabling order loading for Lightspeed sales channel failed',
        _myparcel_sales_channel_id: salesChannelId,
      });

      return undefined;
    }
  };

  const fetchLightspeedSalesChannelById = async (
    salesChannelId: string,
  ): Promise<LightspeedSalesChannel | undefined> => {
    const client = useLightspeedEcommerceService();

    try {
      const response = await client.getSalesChannelById(salesChannelId);

      const lightspeedSalesChannel: LightspeedSalesChannel = {
        ...response,
        ecommercePlatform: 'LIGHTSPEED',
      };

      salesChannelStore.addSalesChannel(lightspeedSalesChannel);

      return lightspeedSalesChannel;
    } catch (error) {
      logToMonitor(error as Error, {
        _myparcel_extra_info: 'Fetching Lightspeed sales channel by id failed',
        _myparcel_sales_channel_id: salesChannelId,
      });

      throw error;
    }
  };

  const requestDelegation = async (salesChannelId: string): Promise<PostRequestDelegationResponse | undefined> => {
    const client = useLightspeedEcommerceService();

    const response = await client.requestDelegation(salesChannelId);

    salesChannelStore.updateSalesChannelToDelegationRequested(response.id, {
      authorizeUrl: response.authorizeUrl,
    });

    return response;
  };

  const createApiKeyCredentials = async (
    salesChannelId: EcommerceSalesChannel['id'],
    credentials: DelegationApiKeyCredentials,
  ): Promise<PostRegisterApiKeyResponse> => {
    const client = useDelegationService();

    const result = await client.createDelegationRequestsApiKey({
      apiKey: credentials,
      delegateeId: {
        delegatee: 'LIGHTSPEED',
        id: salesChannelId,
      },
    });

    return result;
  };

  const importLightspeedProducts = async (saleschannelIds: ImportProductsRequest['salesChannelIds']) => {
    const client = useLightspeedEcommerceService();

    try {
      const response = await client.importProducts(saleschannelIds);
      return response;
    } catch (error) {
      logToMonitor(error as Error, {
        _myparcel_extra_info: 'Importing Lightspeed products failed in useLightspeed composable',
        _myparcel_sales_channel_ids: saleschannelIds,
      });
      throw error;
    }
  };

  return {
    getLightspeedSalesChannels,
    createLightspeedSalesChannel,
    removeLightspeedSalesChannel,
    renameSalesChannel,
    enableOrderLoading,
    disableOrderLoading,
    importLightspeedProducts,
    requestDelegation,
    createApiKeyCredentials,
    fetchLightspeedSalesChannelById,
  };
}
